import { sanitizeIdentifier } from "../../codegen/emit-function-graph.js";
/**
 * Check if a string is a valid JavaScript identifier.
 */
export function isValidIdentifierName(name) {
    return /^[A-Za-z_$][A-Za-z0-9_$]*$/.test(name);
}
/**
 * Check if a segment is an array-slot segment (bracketed identifier).
 */
export function isArraySlotSegment(seg) {
    return seg.bracketed && isValidIdentifierName(seg.key);
}
/**
 * Check if a path ends with an array-slot segment.
 */
export function isArraySlotPath(path) {
    const segments = splitPath(path);
    return segments.length > 0 && isArraySlotSegment(segments[segments.length - 1]);
}
/**
 * Split a property path into segments, handling dot and bracket notation.
 * Returns PathSegment[] with bracketed flag to distinguish [key] vs key notation.
 * Examples:
 *   "user.name" → [{key: "user", bracketed: false}, {key: "name", bracketed: false}]
 *   "items[0].name" → [{key: "items", bracketed: false}, {key: "0", bracketed: true}, {key: "name", bracketed: false}]
 *   "list[a]" → [{key: "list", bracketed: false}, {key: "a", bracketed: true}]
 *   "[0]" → [{key: "0", bracketed: true}]
 */
export function splitPath(path) {
    if (!path)
        return [];
    const segments = [];
    let current = "";
    let i = 0;
    while (i < path.length) {
        if (path[i] === "[") {
            // Handle bracket notation [key] or [0]
            if (current) {
                segments.push({ key: current, bracketed: false });
                current = "";
            }
            const closeIdx = path.indexOf("]", i);
            if (closeIdx === -1)
                break;
            segments.push({ key: path.substring(i + 1, closeIdx), bracketed: true });
            i = closeIdx + 1;
        }
        else if (path[i] === ".") {
            // Handle dot notation
            if (current) {
                segments.push({ key: current, bracketed: false });
                current = "";
            }
            i++;
        }
        else {
            current += path[i];
            i++;
        }
    }
    if (current) {
        segments.push({ key: current, bracketed: false });
    }
    return segments;
}
/**
 * Compute the resolved variable name for each field, handling aliases and collisions.
 * For array-slot fields (final segment is bracketed identifier), the bracket name IS the var name.
 * Aliases are authoritative and take precedence for non-slot fields; field-array order is the
 * tie-break for numeric collision suffixes. Slot names win on collision ties.
 */
export function computeFieldVarNames(fields) {
    const varNames = new Map();
    const usedNames = new Set();
    // Pass 1: extract slot names (these win on collisions)
    for (const field of fields) {
        if (isArraySlotPath(field.path)) {
            const segments = splitPath(field.path);
            const lastSegment = segments[segments.length - 1];
            const slotName = lastSegment.key;
            varNames.set(field.id, slotName);
            usedNames.add(slotName);
        }
    }
    // Pass 2: seed with every non-blank, trimmed alias for non-slot fields
    for (const field of fields) {
        if (varNames.has(field.id))
            continue; // Already a slot
        if (field.alias?.trim()) {
            const alias = field.alias.trim();
            if (usedNames.has(alias)) {
                throw new Error(`Duplicate alias "${alias}" across multiple fields in destruct node`);
            }
            varNames.set(field.id, alias);
            usedNames.add(alias);
        }
    }
    // Pass 3: for every field without an alias/slot: compute auto name and handle collisions
    for (const field of fields) {
        if (varNames.has(field.id))
            continue; // Already has alias or is a slot
        const segments = splitPath(field.path);
        let baseName = "";
        if (segments.length > 0) {
            const lastSegment = segments[segments.length - 1];
            // If last segment is a valid identifier, use it
            if (isValidIdentifierName(lastSegment.key)) {
                baseName = lastSegment.key;
            }
            else {
                // Otherwise, sanitize the full path as a joined name
                baseName = sanitizeIdentifier(segments.map((s) => s.key).join("_"));
            }
        }
        if (!baseName) {
            baseName = "value";
        }
        // Handle collisions with numeric suffixes
        let finalName = baseName;
        let suffix = 2;
        while (usedNames.has(finalName)) {
            finalName = `${baseName}_${suffix}`;
            suffix++;
        }
        varNames.set(field.id, finalName);
        usedNames.add(finalName);
    }
    return varNames;
}
/**
 * Build a destructuring tree and validate for structural errors.
 * Returns { root, errors } where errors is an array of error messages (empty if valid).
 * Detects:
 * - Mid-path array-slot bracket (only final segment allowed)
 * - Root-level array-slot bracket (needs parent key)
 * - Duplicate slot name within same array group
 * - Field-shape conflict (plain value/object vs array group at same key)
 *
 * Key insight: Array-slot fields like `user.hobbies[a]` cause the PARENT node
 * (hobbies) to become an array group, with slots collected at that parent level.
 * A `user.hobbies[a]` field means: navigate to user.hobbies, mark it isArrayGroup=true,
 * and add a slot entry. Multiple fields `user.hobbies[a]`, `user.hobbies[b]` all
 * contribute slots to the same `hobbies` node.
 */
export function buildDestructTree(fields, varNames) {
    const errors = [];
    const root = { key: "__root__" };
    for (let declOrder = 0; declOrder < fields.length; declOrder++) {
        const field = fields[declOrder];
        const segments = splitPath(field.path);
        const varName = varNames.get(field.id);
        if (!varName)
            continue;
        // Check for root-level array-slot (no parent key)
        if (segments.length === 1 && isArraySlotSegment(segments[0])) {
            errors.push(`Field "${field.path}" uses array-slot bracket at root level — needs a parent key (e.g., "list[a]", not just "[a]")`);
            continue;
        }
        // Check for mid-path array-slot
        for (let i = 0; i < segments.length - 1; i++) {
            if (isArraySlotSegment(segments[i])) {
                errors.push(`Field "${field.path}" uses array-slot bracket in non-final position (segment ${i}) — only final segment allowed`);
                continue;
            }
        }
        const isFinalSegmentSlot = segments.length > 0 && isArraySlotSegment(segments[segments.length - 1]);
        // Navigate/create tree nodes
        let node = root;
        const navigateUpTo = isFinalSegmentSlot ? segments.length - 1 : segments.length;
        for (let i = 0; i < navigateUpTo; i++) {
            const segment = segments[i];
            if (!node.children) {
                node.children = new Map();
            }
            if (!node.children.has(segment.key)) {
                node.children.set(segment.key, { key: segment.key });
            }
            const nextNode = node.children.get(segment.key);
            node = nextNode;
        }
        // After navigation, handle the leaf (or array-group root)
        if (isFinalSegmentSlot) {
            // This is an array-slot field: `node` is now the array-group parent,
            // and the final segment contains the slot name
            const slotSegment = segments[segments.length - 1];
            const slotName = slotSegment.key; // "a", "b", "x", etc.
            // Check for conflict before modifying state
            if (node.isLeaf) {
                // Conflict: was a plain field, now trying to be an array group
                errors.push(`Field "${field.path}" conflicts with a plain field at the same key`);
                continue;
            }
            if (!node.isArrayGroup) {
                node.isArrayGroup = true;
                node.arraySlots = [];
            }
            // Add slot to the array group
            node.arraySlots.push({ varName, declOrder });
        }
        else {
            // Regular (non-slot) leaf
            if (node.isArrayGroup) {
                // Conflict: already committed to array group
                errors.push(`Field "${field.path}" conflicts with array-slot fields at the same key`);
                continue;
            }
            node.varName = varName;
            node.isLeaf = true;
        }
    }
    // Check for duplicate slot names within each array group
    const visitNode = (node) => {
        if (node.arraySlots && node.arraySlots.length > 0) {
            const slotNames = new Set();
            for (const slot of node.arraySlots) {
                if (slotNames.has(slot.varName)) {
                    errors.push(`Duplicate slot name "${slot.varName}" in array group at "${node.key}"`);
                }
                slotNames.add(slot.varName);
            }
        }
        if (node.children) {
            for (const child of node.children.values()) {
                visitNode(child);
            }
        }
    };
    visitNode(root);
    return { root, errors };
}
/**
 * Build a single nested object-destructuring pattern across all fields,
 * including support for array-slot nested destructuring.
 * Returns the pattern string (e.g., "{ user, user: { hobbies: [a, b] = [] } = {} }")
 * ready to use in a destructuring statement.
 * Throws if structural errors are detected.
 */
export function buildObjectDestructuringPattern(fields, varNames) {
    if (fields.length === 0)
        return "";
    // Build and validate tree
    const { root, errors } = buildDestructTree(fields, varNames);
    if (errors.length > 0) {
        throw new Error(`Destruct tree validation failed:\n${errors.join("\n")}`);
    }
    // Mark nodes that are both leaves AND have children (edge case from plan §2)
    const markLeafAndBranch = (node) => {
        if (node.children) {
            for (const child of node.children.values()) {
                markLeafAndBranch(child);
            }
        }
        if (node.isLeaf && node.children && node.children.size > 0) {
            node.isLeafAndBranch = true;
        }
    };
    markLeafAndBranch(root);
    // Recursively emit the pattern
    const emitPattern = (node) => {
        // Check for array-slot group (before checking children)
        if (node.isArrayGroup && node.arraySlots && node.arraySlots.length > 0) {
            const slots = node.arraySlots
                .sort((a, b) => a.declOrder - b.declOrder)
                .map((s) => s.varName)
                .join(", ");
            const inner = `[${slots}]`;
            const key = node.key;
            if (needsQuoting(key)) {
                return `"${key}": ${inner} = []`;
            }
            return `${key}: ${inner} = []`;
        }
        if (!node.children || node.children.size === 0) {
            // Leaf: emit the key or key: varName
            const key = node.key;
            if (needsQuoting(key)) {
                return `"${key}": ${node.varName || key}`;
            }
            if (node.varName && node.varName !== key) {
                return `${key}: ${node.varName}`;
            }
            return key; // Shorthand
        }
        // Branch: emit nested destructuring
        const patterns = [];
        for (const child of node.children.values()) {
            patterns.push(emitPattern(child));
        }
        const nested = `{ ${patterns.join(", ")} } = {}`;
        // If this node is also a leaf (has both varName and children), emit it twice
        if (node.isLeafAndBranch && node.varName) {
            const key = node.key;
            if (needsQuoting(key)) {
                return `"${key}", "${key}": ${nested}`;
            }
            return `${key}, ${key}: ${nested}`;
        }
        // Normal branch: emit as key: { ... }
        const key = node.key;
        if (needsQuoting(key)) {
            return `"${key}": ${nested}`;
        }
        return `${key}: ${nested}`;
    };
    // Collect patterns from root's children
    if (!root.children || root.children.size === 0)
        return "";
    const patterns = [];
    for (const child of root.children.values()) {
        patterns.push(emitPattern(child));
    }
    return `{ ${patterns.join(", ")} }`;
}
/**
 * Check if a key needs to be quoted in a destructuring pattern
 * (numeric or contains special characters).
 */
function needsQuoting(key) {
    return /^\d/.test(key) || !/^[A-Za-z_$][A-Za-z0-9_$]*$/.test(key);
}
const DESTRUCT_VARIABLE_PREFIX = "destruct:";
/** Stable id for a Destruct field's virtual variable: `destruct:<destructNodeId>:<fieldId>`. */
export function destructVariableId(destructNodeId, fieldId) {
    return `${DESTRUCT_VARIABLE_PREFIX}${destructNodeId}:${fieldId}`;
}
/** Inverse of `destructVariableId`, or `null` if `id` isn't one (an ordinary declared
 * variable's id, generated by `generateVariableId()`, never starts with this prefix). */
export function parseDestructVariableId(id) {
    if (!id.startsWith(DESTRUCT_VARIABLE_PREFIX))
        return null;
    const rest = id.slice(DESTRUCT_VARIABLE_PREFIX.length);
    const sep = rest.indexOf(":");
    if (sep === -1)
        return null;
    return { destructNodeId: rest.slice(0, sep), fieldId: rest.slice(sep + 1) };
}
/**
 * Synthesizes a `VariableDeclaration` for every `let`/`var` field of every `logic.destruct`
 * node in `nodes` — the "virtual variable" pool `variable.get`/`variable.set` resolve
 * against in addition to `flow.variables`/`graph.variables`. `const` Destruct nodes
 * contribute nothing (see phase-45 doc §2.1). `dataType` is always `"any"` — a destructured
 * field has no declared type anywhere in this codebase.
 */
export function collectDestructVirtualVariables(nodes) {
    const result = [];
    for (const node of nodes) {
        if (node.type !== "logic.destruct")
            continue;
        const data = node.data;
        const declarationKind = data?.declarationKind === "let" || data?.declarationKind === "var"
            ? data.declarationKind
            : "const";
        if (declarationKind === "const")
            continue;
        const fields = Array.isArray(data?.fields)
            ? data.fields
            : [];
        let varNames;
        try {
            varNames = computeFieldVarNames(fields);
        }
        catch {
            continue; // mid-edit duplicate alias — same tolerance effectivePorts.ts already has
        }
        for (const field of fields) {
            const varName = varNames.get(field.id);
            if (!varName)
                continue;
            result.push({
                id: destructVariableId(node.id, field.id),
                name: varName,
                keyword: declarationKind,
                dataType: "any",
            });
        }
    }
    return result;
}
/**
 * Build an array-destructuring pattern for array mode.
 * Sorts fields by numeric position and fills holes with commas.
 * Example: fields at positions 0, 2 → "[a, , c, ...rest]"
 */
export function buildArrayDestructuringPattern(fields, varNames, hasRest, restVar) {
    if (fields.length === 0 && !hasRest)
        return "";
    // Convert path to numeric position and sort
    const positioned = fields
        .map((f) => {
        const segments = splitPath(f.path);
        // First segment should be the numeric position in array mode
        const pos = segments.length > 0 ? parseInt(segments[0].key, 10) : NaN;
        return {
            pos,
            varName: varNames.get(f.id),
        };
    })
        .filter((p) => !isNaN(p.pos))
        .sort((a, b) => a.pos - b.pos);
    if (positioned.length === 0 && !hasRest)
        return "";
    const slots = [];
    if (positioned.length > 0) {
        const maxPos = positioned[positioned.length - 1].pos;
        for (let i = 0; i <= maxPos; i++) {
            const found = positioned.find((p) => p.pos === i);
            if (found) {
                slots.push(found.varName || `_${i}`);
            }
            else {
                slots.push(""); // Hole
            }
        }
    }
    if (hasRest && restVar) {
        slots.push(`...${restVar}`);
    }
    return `[${slots.join(", ")}]`;
}
//# sourceMappingURL=destruct-naming.js.map